import React from "react"
import { graphql } from "gatsby"
import Slider from "react-slick"
import { useWindowDimensions } from "hooks/useWindowDimensions"
import { StrapiImageItem } from "_types/AssetsImage"
import { Image } from "components/image/Image"
import { ImageLink } from "components/image-link/ImageLink"

import "./affiliation-slider.module.scss"
import "slick-carousel/slick/slick-theme.css"

interface AffiliationProps {
  heading: string | null
  sub_heading: string | null
  logo_slider_description?: {
    data: { logo_slider_description: string }
  } | null
  logos?: {
    image: StrapiImageItem
    id: string
    external_link: string
    page: { slug: string }
  }[]
}

export const AffiliationSlider = ({
  heading,
  sub_heading,
  logos,
}: AffiliationProps) => {
  const { size } = useWindowDimensions()
  const getSlides = () => (logos && logos?.length >= 1 && size > 768 ? 2 : 1)
  const settings = {
    dots: false,
    infinite: true,
    slidesToShow: getSlides(),
    slidesToScroll: 1,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 3000,
  }
  return (
    <section className="affiliation-slider u-content-block-padding">
      <div className="fr-container">
        {(heading || sub_heading) && (
          <div className="affiliation-slider__title">
            {heading && <h2>{heading}</h2>}
            {sub_heading && <h3>{sub_heading}</h3>}
          </div>
        )}

        {size < 1024 && logos && logos?.length > 0 && (
          <Slider {...settings}>
            {logos?.map(({ id, external_link, page, image }, index) => {
              if (external_link || page) {
                return (
                  <ImageLink
                    key={`${id}-${index}`}
                    image={image}
                    page={page}
                    external_link={external_link}
                  />
                )
              } else
                return (
                  <Image
                    key={`${id}-${index}`}
                    image={image}
                    alt={image.alternativeText ?? ""}
                  />
                )
            })}
          </Slider>
        )}

        {size >= 1024 && logos && logos?.length > 0 && (
          <div className="affiliation-slider__grid">
            {logos.map(({ id, external_link, page, image }, index) => {
              if (external_link || page) {
                return (
                  <ImageLink
                    key={`${id}-${index}`}
                    image={image}
                    page={page}
                    external_link={external_link}
                  />
                )
              } else
                return (
                  <Image
                    key={`${id}-${index}`}
                    image={image}
                    alt={image.alternativeText ?? ""}
                  />
                )
            })}
          </div>
        )}
      </div>
    </section>
  )
}

export const strapiContentBlockLogoSliderQuery = graphql`
  fragment StrapiLogoSliderFragment on STRAPI__COMPONENT_CONTENT_BLOCK_LOGO_SLIDER {
    id
    strapi_component
    heading
    sub_heading
    logos {
      id
      external_link
      caption
      title
      image {
        ...StrapiImageFragment
      }
      page {
        slug
      }
    }
  }
`

export const strapiAffiliationSliderGlobalFooterQuery = graphql`
  fragment StrapiLogoSliderGlobalFooterFragment on STRAPI__COMPONENT_CONTENT_BLOCK_FOOTER_LOGO_SLIDER {
    strapi_component
    id
    logo_slider {
      logo_slider {
        eyebrow_text
        heading
        sub_heading
        logo_slider_description {
          data {
            logo_slider_description
          }
        }
        logos {
          id
          caption
          external_link
          title
          image {
            alternativeText
            height
            url
            width
          }
          page {
            slug
          }
        }
      }
    }
  }
`
